'use client';

import { useState, useCallback } from 'react';
import { useConnection, usePublicClient } from 'wagmi';
import { Address, parseUnits, formatUnits } from 'viem';
import { readContract, writeContract, waitForTransactionReceipt } from '@wagmi/core';
import { config } from '@/config/index';
import { ChainType } from '@/constants/chain-specific';
import type { TokenConfigType } from '@/constants/types/currency';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';

// Standard ERC20 ABI for approval and allowance
const ERC20_ABI = [{
  inputs: [{
    name: 'owner',
    type: 'address'
  }, {
    name: 'spender',
    type: 'address'
  }],
  name: 'allowance',
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    name: 'spender',
    type: 'address'
  }, {
    name: 'amount',
    type: 'uint256'
  }],
  name: 'approve',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}] as const;
export interface TokenApprovalState {
  isLoading: boolean;
  error: string | null;
  txHash: string | null;
  isApproved: boolean;
}
export interface ApprovalParams {
  token: TokenConfigType;
  amount: string;
  spender: Address;
  chainId: ChainType;
}

/**
 * Hook for managing token approvals for liquidity operations
 */
export function useTokenApproval() {
  const {
    address
  } = useConnection();
  const publicClient = usePublicClient();
  const [state, setState] = useState<TokenApprovalState>({
    isLoading: false,
    error: null,
    txHash: null,
    isApproved: false
  });
  const resetState = useCallback(() => {
    setState({
      isLoading: false,
      error: null,
      txHash: null,
      isApproved: false
    });
  }, []);

  /**
   * Check if token is approved for the required amount
   */
  const checkApproval = useCallback(async (params: ApprovalParams): Promise<boolean> => {
    if (!address) return false;
    try {
      const {
        token,
        amount,
        spender,
        chainId
      } = params;
      const allowance = await readContract(config, {
        address: token.address as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [address, spender],
        chainId
      });
      const requiredAmount = parseUnits(amount, token.decimals);
      const isApproved = allowance >= requiredAmount;
      setState(prev_0 => ({
        ...prev_0,
        isApproved
      }));
      return isApproved;
    } catch (error) {
      console.error('Error checking approval:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to check token approval',
        isApproved: false
      }));
      return false;
    }
  }, [address]);

  /**
   * Approve token for spending
   */
  const approve = useCallback(async (params_0: ApprovalParams): Promise<boolean> => {
    if (!address) {
      setState(prev_1 => ({
        ...prev_1,
        error: 'Wallet not connected'
      }));
      return false;
    }
    try {
      setState(prev_3 => ({
        ...prev_3,
        isLoading: true,
        error: null
      }));
      const {
        token: token_0,
        amount: amount_0,
        spender: spender_0,
        chainId: chainId_0
      } = params_0;

      // Use a high allowance for better UX (avoids repeated approvals)
      const approvalAmount = parseUnits('1000000000', token_0.decimals); // 1B tokens

      console.log(`🔄 Approving ${token_0.symbol} for spending...`);
      const txHash = await writeContract(config, {
        address: token_0.address as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [spender_0, approvalAmount],
        chainId: chainId_0
      });
      setState(prev_4 => ({
        ...prev_4,
        txHash
      }));

      // Wait for transaction confirmation
      const receipt = await waitForTransactionReceipt(config, {
        hash: txHash,
        chainId: chainId_0
      });
      if (receipt.status === 'success') {
        setState(prev_5 => ({
          ...prev_5,
          isLoading: false,
          isApproved: true
        }));
        console.log(`✅ ${token_0.symbol} approved successfully`);
        return true;
      } else {
        throw new Error('Transaction failed');
      }
    } catch (error_0) {
      console.error('❌ Approval failed:', error_0);
      setState(prev_2 => ({
        ...prev_2,
        isLoading: false,
        error: error_0 instanceof Error ? error_0.message : 'Approval failed',
        isApproved: false
      }));
      return false;
    }
  }, [address]);

  /**
   * Check and approve tokens if needed for liquidity operations
   */
  const ensureApprovals = useCallback(async (token0: TokenConfigType, token1: TokenConfigType, amount0: string, amount1: string, dexSlug: string, chainId_1: ChainType): Promise<boolean> => {
    if (!address) return false;
    try {
      const dex = DEX_LIST[dexSlug];
      if (!dex) throw new Error(`DEX ${dexSlug} not found`);
      const spender_1 = dex.router.address as Address;
      console.log(`🔍 Checking approvals for ${token0.symbol} and ${token1.symbol}`);

      // Check both tokens
      const [token0Approved, token1Approved] = await Promise.all([checkApproval({
        token: token0,
        amount: amount0,
        spender: spender_1,
        chainId: chainId_1
      }), checkApproval({
        token: token1,
        amount: amount1,
        spender: spender_1,
        chainId: chainId_1
      })]);

      // Approve tokens if needed
      const approvals: Promise<boolean>[] = [];
      if (!token0Approved) {
        console.log(`⏳ ${token0.symbol} needs approval`);
        approvals.push(approve({
          token: token0,
          amount: amount0,
          spender: spender_1,
          chainId: chainId_1
        }));
      }
      if (!token1Approved) {
        console.log(`⏳ ${token1.symbol} needs approval`);
        approvals.push(approve({
          token: token1,
          amount: amount1,
          spender: spender_1,
          chainId: chainId_1
        }));
      }
      if (approvals.length === 0) {
        console.log(`✅ All tokens already approved`);
        return true;
      }

      // Wait for all approvals
      const results = await Promise.all(approvals);
      const allApproved = results.every(result => result);
      if (allApproved) {
        console.log(`✅ All approvals completed successfully`);
      } else {
        console.log(`❌ Some approvals failed`);
      }
      return allApproved;
    } catch (error_1) {
      console.error('❌ Approval check/execution failed:', error_1);
      setState(prev_6 => ({
        ...prev_6,
        error: error_1 instanceof Error ? error_1.message : 'Approval process failed'
      }));
      return false;
    }
  }, [address, checkApproval, approve]);
  return {
    state,
    checkApproval,
    approve,
    ensureApprovals,
    resetState
  };
}